import useInitialConfigStore from "../storeModules/useInitialConfigStore";
import getMycoins from "./getMycoins";

export const LOCAL_STORAGE_SESSION_CONFIG_KEY = "mycon_session_config";

export const SESSION_CONFIG_KEYS = [
  "compraId",
  "bannerSrc",
  "desconto",
  "tipoTabela",
  "siteDomain",
  "brand",
];

export const normalizeSiteDomain = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) return "";

  try {
    const url = new URL(raw.includes("://") ? raw : `https://${raw}`);
    const path = url.pathname.replace(/\/$/, "");
    return `${url.origin}${path}`.toLowerCase();
  } catch {
    return raw.replace(/\/$/, "").toLowerCase();
  }
};

const normalizeConfigValue = (key, value) => {
  if (key === "desconto") return !!value;
  if (key === "compraId") return Number(value) || 0;
  if (key === "siteDomain") return normalizeSiteDomain(value);
  if (key === "brand") return String(value ?? "").trim().toLowerCase();
  return String(value ?? "");
};

export const getCurrentSessionConfig = () => {
  const { compraId, bannerSrc, desconto, tipoTabela, siteDomain, brand } =
    useInitialConfigStore.getState();

  return {
    compraId,
    bannerSrc,
    desconto,
    tipoTabela,
    siteDomain,
    brand,
  };
};

export const persistSessionConfig = () => {
  try {
    localStorage.setItem(
      LOCAL_STORAGE_SESSION_CONFIG_KEY,
      JSON.stringify(getCurrentSessionConfig()),
    );
  } catch (_) {}
};

export const clearSessionConfigStorage = () => {
  localStorage.removeItem(LOCAL_STORAGE_SESSION_CONFIG_KEY);
};

const hasStoredConfigMismatch = (stored) => {
  const current = getCurrentSessionConfig();

  return SESSION_CONFIG_KEYS.some((key) => {
    if (!(key in stored)) return false;
    return (
      normalizeConfigValue(key, stored[key]) !==
      normalizeConfigValue(key, current[key])
    );
  });
};

const getConversationIdentity = (conversacao = {}) => {
  const metadata = conversacao.metadata ?? {};

  return {
    siteDomain: conversacao.site_domain ?? metadata.site_domain ?? "",
    brand: conversacao.brand ?? metadata.brand ?? "",
  };
};

const hasConversationIdentityMismatch = (conversacao = {}) => {
  const { siteDomain: storedSiteDomain, brand: storedBrand } =
    getConversationIdentity(conversacao);

  if (!storedSiteDomain && !storedBrand) return false;

  const { siteDomain, brand } = useInitialConfigStore.getState();

  if (
    storedSiteDomain &&
    normalizeSiteDomain(storedSiteDomain) !== normalizeSiteDomain(siteDomain)
  ) {
    return true;
  }

  if (
    storedBrand &&
    normalizeConfigValue("brand", storedBrand) !==
      normalizeConfigValue("brand", brand)
  ) {
    return true;
  }

  return false;
};

const hasMetadataConfigMismatch = (metadata = {}) => {
  const current = getCurrentSessionConfig();
  const { referralcode } = useInitialConfigStore.getState();

  return (
    !!metadata.desconto !== !!current.desconto ||
    (metadata.referralcode || "") !== (referralcode || "") ||
    !!metadata.mycoins !== !!getMycoins()
  );
};

export const hasSessionConfigMismatch = (conversacao = {}) => {
  const metadata =
    conversacao.metadata ??
    (conversacao.site_domain || conversacao.brand ? {} : conversacao);

  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_SESSION_CONFIG_KEY);
    if (raw) {
      if (hasStoredConfigMismatch(JSON.parse(raw))) return true;
    }
  } catch (_) {}

  if (hasConversationIdentityMismatch(conversacao)) return true;

  if (metadata && Object.keys(metadata).length > 0) {
    return hasMetadataConfigMismatch(metadata);
  }

  return false;
};
